import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, HistoricalOrder } from '../api';
import { customerEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';
import { CustomerRecordPanel, TagBadges, TagKindsPanel } from '../components/CustomerRecord';
import { CustomerCreditPanel } from '../components/CustomerCredit';
import { useFeatures } from '../features';
import { dateWords } from '../terms';

/**
 * Customers (Phase 12): who buys, what they buy, whether they show up.
 * Search finds anyone with a completed order (an account, or a guest by
 * email); the detail page is everything the facts hold about them.
 */

/**
 * What an order was **for**, in one line (D-253).
 *
 * This printed `MIN(event_title_snapshot)`, so a season package of seven
 * nights read as whichever of its events sorted first alphabetically — a
 * forty-two ticket order labeled "Andy Beningo", which is true of one
 * forty-second of it.
 *
 * A package has a name and that is the answer. Without one, several events are
 * counted rather than picked between, because choosing one arbitrarily is what
 * made this wrong in the first place.
 */
function orderSubject(order: Record<string, unknown>): string {
  const packageName = order['package_name'] as string | null;
  if (packageName) return packageName;
  const title = order['event_title'] as string | null;
  const count = Number(order['event_count'] ?? 0);
  if (!title) return '—';
  return count > 1 ? `${title} +${count - 1} more` : title;
}

export default function CustomersPage() {
  const { key } = useParams();
  const can = usePermissions();
  if (!can('customers.lookup')) return <NotPermitted permission="customers.lookup" />;
  if (key) return <CustomerDetail customerKey={decodeURIComponent(key)} />;
  return <CustomerSearch />;
}

function CustomerSearch() {
  const can = usePermissions();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  // Narrow to one tag (D-358): everybody tagged Board member, say, before the gala.
  const [tag, setTag] = useState('');
  const results = useQuery({ queryKey: ['customers', submitted, tag], queryFn: () => customerEndpoints.search(submitted, tag || undefined) });
  // The previous system's history is a hidden feature (D-369): no column for it unless it is on.
  const history = useFeatures().history_import;
  const kinds = useQuery({ queryKey: ['customers', 'tag-kinds'], queryFn: () => customerEndpoints.tagKinds() });
  return (
    <div className="space-y-4">
    <Panel title="Customers" description="Ranked by what they have spent. Search by name, email, phone or order number; leave it blank for the top customers. A search also finds people who only ever bought in the previous system, by name, email or old order number.">
      <form
        className="flex gap-2 border-b border-line px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(q);
        }}
      >
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or order number" className={`${INPUT} w-full max-w-md`} aria-label="Find a customer" />
        <select value={tag} onChange={(e) => setTag(e.target.value)} className={INPUT} aria-label="Only customers tagged">
          <option value="">Any tag</option>
          {(kinds.data?.tags ?? []).map((kind) => (
            <option key={kind.code} value={kind.code}>
              {kind.name}
            </option>
          ))}
        </select>
        <Button type="submit" variant="primary">
          Find
        </Button>
      </form>
      <Table headers={['Customer', 'Orders', 'Tickets', 'Events', 'Admitted', 'Spent', 'Refunded', 'Gifts', ...(history ? ['Historical'] : []), 'Last order']}>
        {results.error ? (
          <EmptyRow columns={history ? 10 : 9}>{(results.error as ApiError).message}</EmptyRow>
        ) : (results.data?.customers ?? []).length === 0 ? (
          <EmptyRow columns={history ? 10 : 9}>{results.data ? 'Nobody matches.' : 'Loading…'}</EmptyRow>
        ) : (
          results.data!.customers.map((c) => (
            <tr key={c.key} className="border-b border-line last:border-0">
              <td className="px-5 py-3 text-sm">
                <Link to={`/customers/${encodeURIComponent(c.key)}`} className="font-medium text-ink-900 hover:underline">
                  {c.name ?? c.email ?? c.key}
                </Link>
                <div className="text-xs text-ink-400">
                  {c.email ?? ''}
                  {c.customer_sub ? ' · account' : ' · guest'}
                </div>
                <TagBadges tags={c.tags} />
              </td>
              <td className="tabular px-5 py-3 text-sm">{c.orders}</td>
              <td className="tabular px-5 py-3 text-sm">{c.tickets}</td>
              <td className="tabular px-5 py-3 text-sm">{c.events}</td>
              <td className="tabular px-5 py-3 text-sm">{c.admitted}</td>
              <td className="tabular px-5 py-3 text-sm">{dollars(c.spend_cents)}</td>
              <td className="tabular px-5 py-3 text-sm">{c.refunded_cents ? dollars(c.refunded_cents) : '—'}</td>
              <td className="tabular px-5 py-3 text-sm">{c.donations_cents ? dollars(c.donations_cents) : '—'}</td>
              {history ? <td className="tabular px-5 py-3 text-sm text-ink-600">{c.history_orders ? `${c.history_orders} · ${dollars(c.history_spend_cents ?? 0)}` : '—'}</td> : null}
              <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(c.last_order_at)}</td>
            </tr>
          ))
        )}
      </Table>
    </Panel>
    {can('customers.tags.manage') ? <TagKindsPanel /> : null}
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="flex-1 rounded-lg border border-line bg-surface px-5 py-4">
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className="tabular mt-1 text-2xl font-bold text-ink-900">{value}</p>
      {detail ? <p className="mt-1 text-sm text-ink-600">{detail}</p> : null}
    </div>
  );
}

function CustomerDetail({ customerKey }: { customerKey: string }) {
  const can = usePermissions();
  const detail = useQuery({ queryKey: ['customers', 'detail', customerKey], queryFn: () => customerEndpoints.detail(customerKey) });
  if (!can('customers.contact.view')) return <NotPermitted permission="customers.contact.view" />;
  if (detail.error) return <ErrorNote>{(detail.error as ApiError).message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-400">Loading…</p>;
  const { summary, orders, events, donations, giving, history, packages, passes, ticket_types: ticketTypes, channels } = detail.data;
  return (
    <div className="space-y-4">
      <Link to="/customers" className="text-sm text-ink-600 hover:underline">
        ← Customers
      </Link>
      <Panel title={summary.name ?? summary.email ?? 'Customer'} description={[summary.email, summary.phone, summary.customer_sub ? `account ${summary.customer_sub}` : 'guest (by email)'].filter(Boolean).join(' · ')}>
        <div className="flex flex-wrap gap-3 px-5 py-4">
          <Stat label="Spent" value={dollars(summary.spend_cents)} detail={`${summary.orders} order${summary.orders === 1 ? '' : 's'}${summary.refunded_cents ? ` · ${dollars(summary.refunded_cents)} refunded` : ''}`} />
          <Stat label="Tickets" value={String(summary.tickets)} detail={`${summary.events} event${summary.events === 1 ? '' : 's'}`} />
          <Stat label="Attendance" value={summary.attendance_pct === null ? '—' : `${summary.attendance_pct}%`} detail={`${summary.admitted} admitted · ${summary.no_shows} no-show${summary.no_shows === 1 ? '' : 's'} on past ${dateWords().many}`} />
          <Stat label="Gifts" value={dollars(summary.donations_cents)} />
          {summary.lifetime_ticket_sales_cents !== undefined ? <Stat label="Order value" value={dollars(summary.lifetime_ticket_sales_cents)} detail={`${dollars(summary.payments_received_cents ?? 0)} received`} /> : null}
          {summary.outstanding_balance_cents ? <Stat label="Balance due" value={dollars(summary.outstanding_balance_cents)} detail={`${summary.open_orders ?? 0} open order${summary.open_orders === 1 ? '' : 's'}${summary.past_due_balance_cents ? ` · ${dollars(summary.past_due_balance_cents)} past due` : ''}`} /> : null}
          {history.totals.orders > 0 ? (
            <Stat
              label="Historical"
              value={dollars(history.totals.spend_cents)}
              detail={`${history.totals.orders} order${history.totals.orders === 1 ? '' : 's'} in the previous system, ${history.totals.first_order_on ?? ''} to ${history.totals.last_order_on ?? ''}`}
            />
          ) : null}
          <Stat label="Since" value={summary.first_order_at ? summary.first_order_at.slice(0, 10) : '—'} detail={`last order ${formatWhen(summary.last_order_at)}${summary.exchanges ? ` · ${summary.exchanges} exchange${summary.exchanges === 1 ? '' : 's'}` : ''}${summary.transfers_out ? ` · ${summary.transfers_out} transferred away` : ''}`} />
        </div>
        <div className="flex flex-wrap gap-6 px-5 pb-4 text-xs text-ink-600">
          <span>By channel: {channels.map((c) => `${humanize(c.channel)} ${c.orders} (${dollars(c.spend_cents)})`).join(' · ') || '—'}</span>
          <span>Ticket types: {ticketTypes.map((t) => `${t.ticket_type ?? '—'} ×${t.tickets}`).join(' · ') || '—'}</span>
        </div>
      </Panel>

      <CustomerRecordPanel customerKey={customerKey} />

      <CustomerCreditPanel customerKey={customerKey} />

      <Panel title="Events" description="What they came to, and whether they came.">
        <Table headers={['Event', `${dateWords().Many}`, 'Tickets', 'Admitted', 'Spent', 'Last']}>
          {events.length === 0 ? (
            <EmptyRow columns={6}>No tickets.</EmptyRow>
          ) : (
            events.map((e) => (
              <tr key={e.event_id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">
                  <Link to={`/events/${e.event_id}`} className="hover:underline">
                    {e.title}
                  </Link>
                </td>
                <td className="tabular px-5 py-3 text-sm">{e.performances}</td>
                <td className="tabular px-5 py-3 text-sm">{e.tickets}</td>
                <td className="tabular px-5 py-3 text-sm">{e.admitted}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(e.spend_cents)}</td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(e.last_starts_at)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {packages.length > 0 || passes.length > 0 ? (
        <Panel title="Packages and passes">
          <Table headers={['Package', 'Kind', 'Status', 'Seats', 'Order', 'Since']}>
            {packages.map((p) => (
              <tr key={p.id as string} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">{p.name as string}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(p.package_kind as string)}</td>
                <td className="px-5 py-3">
                  <Badge tone={p.status === 'ACTIVE' ? 'ok' : 'neutral'}>{humanize(p.status as string)}</Badge>
                  {p.subscription_status ? <div className="text-xs text-ink-400">seats {humanize(p.subscription_status as string).toLowerCase()}</div> : null}
                </td>
                <td className="tabular px-5 py-3 text-sm">{p.seats as number}</td>
                <td className="px-5 py-3 font-mono text-sm">{p.order_number as string}</td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(p.created_at as string)}</td>
              </tr>
            ))}
            {passes.map((p) => (
              <tr key={p.id as string} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">{p.name as string}</td>
                <td className="px-5 py-3 text-sm text-ink-600">Pass</td>
                <td className="px-5 py-3">
                  <Badge tone={p.status === 'ACTIVE' ? 'ok' : 'neutral'}>{humanize(p.status as string)}</Badge>
                </td>
                <td className="tabular px-5 py-3 text-sm">
                  {p.redeemed as number} redeemed{p.total_redemptions_allowed ? ` of ${p.total_redemptions_allowed as number}` : ''}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{p.admissions_per_performance as number} per {dateWords().one}</td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">until {formatWhen(p.valid_to as string)}</td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      <Panel title="Orders">
        <Table headers={['Order', 'Event', 'Channel', 'Tickets', 'Total', 'Refunded', 'Status', 'Date']}>
          {orders.length === 0 ? (
            <EmptyRow columns={8}>No orders.</EmptyRow>
          ) : (
            orders.map((o) => (
              <tr key={o.id as string} className="border-b border-line last:border-0">
                <td className="px-5 py-3 font-mono text-sm">
                  <Link to={`/orders/${o.id as string}`} className="hover:underline">
                    {o.order_number as string}
                  </Link>
                </td>
                <td className="px-5 py-3 text-sm text-ink-900">{orderSubject(o)}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(o.channel as string)}</td>
                <td className="tabular px-5 py-3 text-sm">{o.tickets as number}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(o.cached_total_due_cents as number)}</td>
                <td className="tabular px-5 py-3 text-sm">{(o.cached_refunded_cents as number) ? dollars(o.cached_refunded_cents as number) : '—'}</td>
                <td className="px-5 py-3">
                  <Badge tone={o.status === 'COMPLETED' ? 'ok' : 'warn'}>{humanize(o.status as string)}</Badge>
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(o.completed_at as string | null)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {history.orders.length > 0 ? <HistoricalOrders orders={history.orders} /> : null}

      {giving.gifts > 0 ? (
        <Panel
          title="Giving"
          description="Summed the way the year-end letter sums it, so this screen and the January statement agree. That rule combines gifts by email, which means a household sharing an address is one donor here."
        >
          <div className="flex flex-wrap gap-8 px-5 py-4">
            <Stat label="Lifetime" value={dollars(giving.total_cents)} />
            <Stat label="Gifts" value={`${giving.gifts}`} />
            <Stat label="Best year" value={giving.best_year ? dollars(giving.best_year.total_cents) : '—'} />
            <Stat label="Years running" value={`${giving.consecutive_years}`} />
            <Stat label="First gift" value={giving.first_gift_at ? giving.first_gift_at.slice(0, 4) : '—'} />
            <Stat label="Last gift" value={giving.last_gift_at ? formatWhen(giving.last_gift_at) : '—'} />
          </div>
          {giving.recurring ? (
            <p className="border-t border-line px-5 py-2 text-sm text-ok">A recurring gift is active. Do not put them on a lapsed-donor list.</p>
          ) : null}
          {/*
            Deliberately no recognition level. There is no giving-level table in
            the schema, so naming somebody a Benefactor here would be the admin
            inventing a policy the venue has not set — the numbers a level would
            be computed from are above instead.
          */}
          <Table headers={['Year', 'Gifts', 'Total', 'Deductible']}>
            {giving.years.map((row) => (
              <tr key={row.year} className="border-b border-line last:border-0">
                <td className="tabular px-5 py-3 text-sm text-ink-900">{row.year}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{row.gifts}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(row.total_cents)}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{dollars(row.deductible_cents)}</td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      {donations.length > 0 ? (
        <Panel title="Gifts">
          <Table headers={['Campaign', 'Kind', 'Amount', 'Status', 'Date']}>
            {donations.map((d) => (
              <tr key={d.id as string} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">{d.campaign as string}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(d.kind as string)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(d.amount_cents as number)}</td>
                <td className="px-5 py-3">
                  <Badge tone={d.status === 'SUCCEEDED' || d.status === 'ACTIVE' ? 'ok' : 'neutral'}>{humanize(d.status as string)}</Badge>
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen((d.succeeded_at as string | null) ?? (d.created_at as string))}</td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}
    </div>
  );
}

/**
 * What they bought in the previous system (D-364).
 *
 * Kept apart from the orders above rather than mixed into them: none of these
 * can be opened, refunded or exchanged, and the totals at the top of the page
 * are this system's alone. The card surcharge is shown here, where staff might
 * be asked about it, and folded into the total on the customer's own page.
 */
function HistoricalOrders({ orders }: { orders: HistoricalOrder[] }) {
  return (
    <Panel title="Historical orders" description="From the previous system's sales history. Read-only: there is no ticket or payment here behind any of it.">
      <Table headers={['Order', 'What', 'Tickets', 'Total', 'Status', 'Date']}>
        {orders.map((order) => (
          <tr key={order.order_number} className="border-b border-line align-top last:border-0">
            <td className="px-5 py-3 font-mono text-sm text-ink-600">{order.order_number}</td>
            <td className="px-5 py-3 text-sm text-ink-900">
              <ul className="space-y-0.5">
                {order.items.map((item, index) => (
                  <li key={index}>
                    {item.kind === 'TICKET'
                      ? `${item.name}${item.starts_at ? `, ${formatWhen(item.starts_at)}` : ''}: ${item.quantity} × ${item.ticket_type ?? 'ticket'}`
                      : `${item.kind === 'SURCHARGE' ? 'Card surcharge' : item.kind === 'GIFT' ? `Gift (${item.name})` : item.name}: ${dollars(item.amount_cents)}`}
                  </li>
                ))}
              </ul>
            </td>
            <td className="tabular px-5 py-3 text-sm">{order.tickets}</td>
            <td className="tabular px-5 py-3 text-sm">{dollars(order.total_cents)}</td>
            <td className="px-5 py-3">
              <Badge tone={order.status === 'COMPLETED' ? 'ok' : order.status === 'REFUNDED' ? 'warn' : 'neutral'}>{humanize(order.status)}</Badge>
            </td>
            <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{order.placed_on}</td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}
